import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
  ViewChildren,
} from '@angular/core';

import { StateService, UIRouterGlobals } from '@uirouter/core';
import { TemplateApiService } from 'src/app/api/services/template-api.service';
import { Orientation } from 'src/app/api/services/presentation.d';
import { BigQueryLoggingService } from 'src/app/components/logging/big-query-logging.service';
import { TrackerService } from 'src/app/components/logging/tracker.service';
import { ModalService } from 'src/app/components/modals/modal.service';
import { FeaturesService } from 'src/app/components/plans/features.service';
import {
  ScrollingListFactoryService,
  ScrollingListInstance,
} from 'src/app/components/scrolling-list/services/scrolling-list-factory.service';
import { TemplatesService } from 'src/app/presentations/services/templates.service';
import { EditorService } from '../../services/editor.service';

import { PresentationUtilsService } from 'src/app/presentations/services/presentation-utils.service';
import { TemplateEditorService } from 'src/app/template-editor/services/template-editor.service';

import { ProductDetailsComponent } from '../product-details/product-details.component';

import { MatCheckbox, MatCheckboxChange } from '@angular/material/checkbox';

import { cloneDeep } from 'lodash';
import { FoldersApiService } from 'src/app/api/services/folders-api.service';
import { CompanyStateService } from 'src/app/auth/services/company-state.service';
import { UserStateService } from 'src/app/auth/services/user-state.service';
import { UpgradeNoticeComponent } from 'src/app/components/plans/upgrade-notice/upgrade-notice.component';
import { AiCreditsService } from 'src/app/settings/services/ai-credits.service';
import {
  CAP_ALERTS_INTEGRATION_ID,
  companyIntegrationOpenTarget,
} from 'src/app/settings/services/integrations/integrations-company/integrations-company.routing';

@Component({
  selector: 'add-presentation',
  templateUrl: './add-presentation.component.html',
  styleUrls: ['./add-presentation.component.scss'],
  standalone: false,
})
export class AddPresentationComponent implements OnDestroy, OnInit {
  static readonly USAGE_ALL = 'ALL';
  static readonly USAGE_K12 = 'PRIMARY_SECONDARY_EDUCATION';
  static readonly POWERBI_PRESENTATION = 'full-screen-powerbi_v1';
  static readonly ORIENTATION_CATEGORIES = ['Portrait', 'Landscape'];

  _defaultCount = 1000;

  isEducationCustomer = this.companyStateService.isEducationCustomer();
  isIndustrialCustomer = this.companyStateService.isIndustrialCustomer();
  isTeacher = this.userStateService.isAssignedPublisher();
  showAiButton = signal(
    this.aiCreditsService.canUserUseAI() &&
      this.companyStateService.isAiEnabled(),
  );

  private templateUsage(item: any): number {
    if (!item.usage) {
      return 0;
    }
    const k12Usage = item.usage[AddPresentationComponent.USAGE_K12] || 0;
    return this.companyStateService.isK12Customer()
      ? k12Usage
      : Math.max(
          item.usage[AddPresentationComponent.USAGE_ALL] || 0,
          k12Usage,
        ) - k12Usage;
  }

  search: any = {
    count: this._defaultCount,
    doSearch: () => {
      this._logFilterUsage();

      if (!this.showSharedTemplates) {
        this.products.doSearch();
      } else {
        this.doSharedTemplatesSearch();
      }
    },
    templatesFilter: {},
    onListUpdate: () => {
      if (!this.search.query) {
        const unweighted = [];
        const weighted = (this.products.items.list as any[]).filter((item) => {
          if (item.defaultOrderWeight != null) {
            return true;
          }
          unweighted.push(item);
          return false;
        });
        const other = [];
        const industry = unweighted.filter((item) => {
          if (this.templatesService.isIndustryTemplate(item)) {
            return true;
          }
          other.push(item);
          return false;
        });

        industry.sort((a, b) => {
          return this.templateUsage(b) - this.templateUsage(a);
        });
        other.sort((a, b) => {
          return this.templateUsage(b) - this.templateUsage(a);
        });

        this.products.items.list = [...weighted, ...industry, ...other];
      } else {
        const other = [];
        const industry = (this.products.items.list as any[]).filter((item) => {
          if (this.templatesService.isIndustryTemplate(item)) {
            return true;
          }
          other.push(item);
          return false;
        });

        this.products.items.list = [...industry, ...other];
      }

      if (this._searchTemplate('Blank Presentation')) {
        this.products.items.list.push({
          name: 'Blank Presentation',
          isBlank: true,
        });
      }

      this.filteredProducts = this.products.items.list;

      this._updateProductFilters();
      this.datesAvailable = this.templatesService.loadDates(
        this.filteredProducts,
      );
      this.templatesService.loadUpcomingEvents(4);

      if (
        this.userStateService.isAssignedPublisher() &&
        this.categoryFilters.templateCategories.find(
          (category) => category.name === 'Classroom',
        )
      ) {
        this.search.templatesFilter.templateCategories = ['Classroom'];
        setTimeout(() => {
          this.toggleFilter('templateCategories', 'Classroom');
          this.preloadFilter = true;
        });
      }

      this.doSearch();
    },
  };

  products: ScrollingListInstance = this.scrollingListFactory.create(
    this.listTemplates.bind(this),
    this.search,
  );

  templates: ScrollingListInstance = this.scrollingListFactory.create(
    this.templateApiService.listPresentationTemplates.bind(
      this.templateApiService,
    ),
    {
      sortBy: 'name',
      reverse: false,
      count: this._defaultCount,
      onListUpdate: () => {
        this.doSharedTemplatesSearch();

        this.changeDetectorRef.detectChanges();
      },
    },
  );

  filterConfig = {
    placeholder: 'Search hundreds of templates...',
    id: 'storeProductsSearchInput',
    jumbo: true,
  };

  preloadFilter = false;
  datesAvailable = false;
  templateGroups: { name: string; templates: any[] }[];
  private _selectedDate: Date | null;

  get selectedDate(): Date | null {
    return this._selectedDate;
  }
  set selectedDate(value: Date | null) {
    this._selectedDate = value;
    this._logFilterUsage();
  }

  filterAppleTv = false;
  orientationFilter: Orientation = 'landscape';
  categoryFilters: any = {};
  filteredProducts = [];
  newTemplates = [];
  filteredUpcomingEvents = [];
  filtered = false;
  focusSearch: boolean = false;

  showSharedTemplates = false;
  filteredSharedTemplates = [];

  @ViewChild(UpgradeNoticeComponent) upgradeNotice: UpgradeNoticeComponent;
  @ViewChild('TemplatesGrid') templatesGrid: ElementRef;
  @ViewChildren('FilterCheckbox') filterCheckboxes: ElementRef[];

  get searchFilterKeys() {
    return Object.keys(this.search.templatesFilter);
  }

  get filteredResults() {
    return (
      this.selectedDate ||
      this.filtered ||
      this.search.query ||
      this.filterAppleTv
    );
  }

  get templateFilters() {
    return {
      ...this.search.templatesFilter,
      selectedDate: this.selectedDate
        ? this.templatesService.formatDateName(this.selectedDate)
        : null,
      searchQuery: this.search.query || '',
      appleTv: !!this.filterAppleTv,
    };
  }

  isAlertTemplate(template: any): boolean {
    return template.templateContentTypes?.includes('Common Alert Protocol');
  }

  constructor(
    public templatesService: TemplatesService,
    private changeDetectorRef: ChangeDetectorRef,
    private modalService: ModalService,
    private scrollingListFactory: ScrollingListFactoryService,
    private userStateService: UserStateService,
    protected companyStateService: CompanyStateService,
    private editorService: EditorService,
    private templateEditorService: TemplateEditorService,
    private presentationUtils: PresentationUtilsService,
    private templateApiService: TemplateApiService,
    private trackerService: TrackerService,
    public featuresService: FeaturesService,
    private bigQueryLogging: BigQueryLoggingService,
    private stateService: StateService,
    private uiRouterGlobals: UIRouterGlobals,
    private aiCreditsService: AiCreditsService,
  ) {}

  ngOnInit(): void {
    const params = this.uiRouterGlobals.params;
    if (params.selectedDate) {
      const date = new Date(params.selectedDate);
      if (!isNaN(date.getTime())) {
        this.selectedDate = date;
        this.doSearch();
      }
    }
    this.editorService.presentation.folderId = params.folderId ?? null;
  }

  listTemplates(search, cursor): Promise<{}> {
    if (this.templatesService.templates.length && !this.search.query) {
      setTimeout(() => {
        this.focusSearch = true;
      });
      return Promise.resolve({
        items: this.templatesService.templates,
      });
    } else {
      return this.templateApiService
        .listTemplates(search, cursor)
        .then((result) => {
          if (!this.search.query) {
            this.templatesService.assignTemplates(result.items);
          }
          this.focusSearch = true;
          return result;
        });
    }
  }

  private _isFeaturedTemplate(template) {
    return (
      this.templatesService.isIndustryTemplate(template) &&
      ((this.isTeacher && template.teacherTemplateOfTheWeek) ||
        (!this.isTeacher && template.templateOfTheWeek))
    );
  }

  slugify(value) {
    return value.toLowerCase().replaceAll(' ', '-');
  }

  get sharedTemplatesAvailable(): boolean | null {
    return this.featuresService.isFeatureAvailable(
      FeaturesService.FEATURE_SHARED_TEMPLATES,
    );
  }

  showUpgradePlanModal(): void {
    this.featuresService.showUpgradePlanModal(
      FeaturesService.FEATURE_SHARED_TEMPLATES,
    );
  }

  _templateCategoryFilter(
    templates: any[],
    category: string,
  ): { name: string; count: number }[] {
    const categoryArrays: string[][] = templates.map(
      (templates) => templates[category],
    );
    const categories: string[] = [].concat.apply([], categoryArrays);

    const categoryCounts = {};
    categories.forEach((category) => {
      if (category) {
        if (categoryCounts[category]) {
          categoryCounts[category] += 1;
        } else {
          categoryCounts[category] = 1;
        }
      }
    });

    return Object.keys(categoryCounts)
      .sort()
      .map((category) => {
        return {
          name: category,
          count: categoryCounts[category],
        };
      });
  }

  _updateProductFilters() {
    if (
      !this.categoryFilters.templateCategories &&
      this.products.items.list.length
    ) {
      this.categoryFilters = {
        templateCategories: this._templateCategoryFilter(
          this.products.items.list,
          'templateCategories',
        ).filter(
          (category) =>
            !AddPresentationComponent.ORIENTATION_CATEGORIES.includes(
              category.name,
            ),
        ),
        templateContentTypes: this._templateCategoryFilter(
          this.products.items.list,
          'templateContentTypes',
        ),
      };
    }
  }

  clearDate(): void {
    this.selectedDate = null;
    this.doSearch();
  }

  removeFilter(group: string, filter: string): void {
    if (this.search.templatesFilter[group]) {
      const filterIndex = this.search.templatesFilter[group].indexOf(filter);
      if (filterIndex >= 0) {
        this.search.templatesFilter[group].splice(filterIndex, 1);
        if (this.search.templatesFilter[group].length === 0) {
          delete this.search.templatesFilter[group];
        }
      }
    }
  }

  toggleFilter(group: string, filter: string): void {
    const checkboxId = 'checkbox-' + group + '-' + this.slugify(filter);
    const checkbox = this.filterCheckboxes.find(
      (check) => (check as unknown as MatCheckbox).id === checkboxId,
    );
    if (checkbox) {
      (checkbox as unknown as MatCheckbox).toggle();
    }
  }

  clearFilter(name: string): void {
    const filter = name.split(':');
    this.removeFilter(filter[0], filter[1]);
    this.toggleFilter(filter[0], filter[1]);
    this.doSearch();
  }

  clearAllFilters(): void {
    this.searchFilterKeys.forEach((group) => {
      for (let f = this.search.templatesFilter[group].length - 1; f >= 0; f--) {
        const filter = this.search.templatesFilter[group][f];
        this.removeFilter(group, filter);
        this.toggleFilter(group, filter);
      }
    });
    this.filterAppleTv = false;
    this.clearDate();
  }

  setFilter(checkbox: MatCheckboxChange, group: string, filter: string): void {
    if (checkbox.checked) {
      if (!this.search.templatesFilter[group]) {
        this.search.templatesFilter[group] = [];
      }
      if (this.search.templatesFilter[group].indexOf(filter) < 0) {
        this.search.templatesFilter[group].push(filter);
      }
    } else {
      this.removeFilter(group, filter);
    }
    this._logFilterUsage();
    this.doSearch();
  }

  private _filterTemplate = (template: any): boolean => {
    for (let key of this.searchFilterKeys) {
      if (!template[key]) {
        return false;
      }
      for (let filter of this.search.templatesFilter[key]) {
        if (!template[key].includes(filter)) {
          return false;
        }
      }
    }
    return true;
  };

  private isResponsiveTemplate(template: {
    templateCategories?: string[];
  }): boolean {
    return template.templateCategories?.includes('Responsive') ?? false;
  }

  private _filterByOrientation = (template: any): boolean => {
    if (template.isBlank) {
      return true;
    }
    const categories: string[] = template.templateCategories ?? [];
    if (this.orientationFilter === 'portrait') {
      return (
        categories.includes('Portrait') || categories.includes('Responsive')
      );
    }
    return (
      categories.includes('Responsive') || !categories.includes('Portrait')
    );
  };

  private _filterProductList(
    items: any[],
    predicate: (template: any) => boolean,
  ): any[] {
    if (this.selectedDate) {
      return items.filter((group) => {
        group.templates = group.templates.filter(predicate);
        return group.templates.length > 0;
      });
    }
    return items.filter(predicate);
  }

  private _searchTemplate = (template: any): boolean => {
    return (
      !this.search?.query ||
      JSON.stringify(template)
        .toLowerCase()
        .indexOf(this.search.query.toLowerCase()) !== -1
    );
  };

  private _logTimeout: number;

  ngOnDestroy(): void {
    clearTimeout(this._logTimeout);
  }

  private _logFilterUsage() {
    clearTimeout(this._logTimeout);

    this._logTimeout = setTimeout(() => {
      this.bigQueryLogging.logEvent(
        'Template filter applied',
        JSON.stringify(this.templateFilters),
      );
    }, 2000) as unknown as number;
  }

  doSearch() {
    let filteredProducts: any[];

    if (this.selectedDate) {
      filteredProducts = cloneDeep(this.templatesService.sortedTemplates);
      const index = this.templatesService.nextSortedIndex(this.selectedDate);
      if (index > 0) {
        const removed = filteredProducts.splice(
          0,
          this.templatesService.nextSortedIndex(this.selectedDate),
        );
        filteredProducts.splice(filteredProducts.length, 0, ...removed);
      }
    } else {
      filteredProducts = this.products.items.list;
    }

    if (this.filterAppleTv) {
      filteredProducts = this._filterProductList(
        filteredProducts,
        (item) => item.supportsAppleTV,
      );
    }

    this.filtered = false;
    for (let key of this.searchFilterKeys) {
      if (this.search.templatesFilter[key].length > 0) {
        this.filtered = true;
        break;
      }
    }

    if (this.filtered) {
      filteredProducts = this._filterProductList(filteredProducts, (template) =>
        this._filterTemplate(template),
      );
    }

    filteredProducts = this._filterProductList(filteredProducts, (template) =>
      this._filterByOrientation(template),
    );

    this.filteredUpcomingEvents = this.templatesService.upcomingEvents.filter(
      (template) => this._filterByOrientation(template),
    );

    if (!this.filtered && !this.selectedDate && !this.search.query) {
      this.newTemplates = [];
      filteredProducts = filteredProducts.filter((item) => {
        if (this._isFeaturedTemplate(item)) {
          this.newTemplates.push(item);
          return false;
        }
        return true;
      });
    } else {
      this.newTemplates = [];
    }

    if (this.selectedDate) {
      this.templateGroups = filteredProducts;
      this.filteredProducts = [];
    } else {
      this.filteredProducts = filteredProducts;
    }

    this.changeDetectorRef.detectChanges();
    if (this.templatesGrid?.nativeElement) {
      this.templatesGrid.nativeElement.scrollIntoView?.({
        block: 'start',
        inline: 'nearest',
      });
    }
  }

  doSharedTemplatesSearch() {
    let filteredTemplates;

    if (this.sharedTemplatesAvailable) {
      filteredTemplates = this.templates.items.list;
    } else {
      filteredTemplates = [];
    }

    if (this.search.query) {
      filteredTemplates = filteredTemplates.filter((item) => {
        return this._searchTemplate(item);
      });
    }

    this.filteredSharedTemplates = filteredTemplates;
  }

  setOrientationFilter(orientation: Orientation): void {
    this.orientationFilter = orientation;
    this.doSearch();
  }

  toggleAppleTv() {
    this.filterAppleTv = !this.filterAppleTv;

    this.doSearch();
  }

  clearAppleTv() {
    this.filterAppleTv = false;

    this.doSearch();
  }

  toggleTemplatesSource() {
    this.showSharedTemplates = !this.showSharedTemplates;

    if (!this.showSharedTemplates) {
      this.products.doSearch();
    } else {
      this.trackerService.presentationEvent(
        'Add Presentation from Shared Template',
      );

      this.doSharedTemplatesSearch();
    }
  }

  focusSearchInput() {
    this.focusSearch = false;
    setTimeout(() => {
      this.focusSearch = true;
    });
  }

  requiresUpgrade(item) {
    return (
      item.productId?.includes(AddPresentationComponent.POWERBI_PRESENTATION) &&
      !this.featuresService.isFeatureAvailable(FeaturesService.FEATURE_POWER_BI)
    );
  }

  getProductImageUrl(product: {
    imageUrl?: string;
    portraitUrl?: string;
  }): string | undefined {
    return this.presentationUtils.getProductImageUrl(
      product,
      this.orientationFilter,
    );
  }

  add(item) {
    if (this.requiresUpgrade(item)) {
      this.featuresService.showUpgradePlanModal(
        item.productId.includes(AddPresentationComponent.POWERBI_PRESENTATION)
          ? FeaturesService.FEATURE_POWER_BI
          : '',
      );

      return;
    }

    const params = this.uiRouterGlobals.params;
    const folderId = params.folderId || FoldersApiService.ROOT_FOLDER_ID;

    if (this.isAlertTemplate(item)) {
      const target = companyIntegrationOpenTarget(CAP_ALERTS_INTEGRATION_ID);
      this.stateService.go(target.state, target.params);
    } else if (item.id) {
      if (this.presentationUtils.isHtmlPresentation(item)) {
        this.templateEditorService.copySharedPresentation(item.id, folderId);
      } else {
        this.editorService.copyTemplate(item.id);
      }
    } else {
      const orientation = this.isResponsiveTemplate(item)
        ? this.orientationFilter
        : undefined;

      this.editorService.addFromProduct(
        item,
        {
          ...this.templateFilters,
          addedFrom: 'Template Gallery',
        },
        orientation,
      );
    }
  }

  select(product) {
    if (this.requiresUpgrade(product)) {
      this.featuresService.showUpgradePlanModal(
        product.productId.includes(
          AddPresentationComponent.POWERBI_PRESENTATION,
        )
          ? FeaturesService.FEATURE_POWER_BI
          : '',
      );

      return;
    }
    if (product.id || this.isAlertTemplate(product)) {
      this.add(product);

      return;
    }

    this.modalService
      .showLargeDialog(
        ProductDetailsComponent,
        { product, orientation: this.orientationFilter },
        { panelClass: ['product-preview'] },
      )
      .then(() => {
        this.add(product);
      });
  }
}
